/**
Copyright (C) 2019-2025, Infraclear Inc. All Rights Reserved.
*/

import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import SearchIcon from '@mui/icons-material/Search';
import { fetchUsageSummary, fetchUserUsage, fetchTeamUsage } from './usageService';
import { UsageSummary, UserCostRollup, TeamCostRollup, UserBreakdown } from './types';
import { SnackBarMessage } from '../../../components/snackBar/snackBar';
import './usage.scss';

const formatCurrency = (value: number): string => `$${value.toFixed(4)}`;

const AdminUsage: React.FC = () => {
  const [dateFrom, setDateFrom] = useState<Dayjs>(dayjs().startOf('month'));
  const [dateTo, setDateTo] = useState<Dayjs>(dayjs());
  const [summary, setSummary] = useState<UsageSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  const [userIdInput, setUserIdInput] = useState('');
  const [userRollup, setUserRollup] = useState<UserCostRollup | null>(null);
  const [userLoading, setUserLoading] = useState(false);

  const [teamIdInput, setTeamIdInput] = useState('');
  const [teamRollup, setTeamRollup] = useState<TeamCostRollup | null>(null);
  const [teamLoading, setTeamLoading] = useState(false);

  const [msgConfig, setMsgConfig] = useState({
    type: '',
    message: '',
    snackFlag: false,
  });

  const getFromStr = () => dateFrom.format('YYYY-MM-DD');
  const getToStr = () => dateTo.format('YYYY-MM-DD');

  const loadSummary = () => {
    setSummaryLoading(true);
    fetchUsageSummary(getFromStr(), getToStr())
      .then((data) => {
        setSummary(data);
      })
      .catch(() => {
        setMsgConfig({
          type: 'error',
          message: 'Failed to load usage summary.',
          snackFlag: true,
        });
      })
      .finally(() => {
        setSummaryLoading(false);
      });
  };

  useEffect(() => {
    loadSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDateChange = () => {
    loadSummary();
    // Clear individual lookups when date range changes
    setUserRollup(null);
    setTeamRollup(null);
  };

  const handleUserLookup = () => {
    const userId = parseInt(userIdInput, 10);
    if (isNaN(userId)) {
      setMsgConfig({
        type: 'error',
        message: 'Please enter a valid numeric User ID.',
        snackFlag: true,
      });
      return;
    }
    setUserLoading(true);
    fetchUserUsage(userId, getFromStr(), getToStr())
      .then((data) => {
        setUserRollup(data);
      })
      .catch(() => {
        setMsgConfig({
          type: 'error',
          message: 'Failed to load user usage. Check the User ID.',
          snackFlag: true,
        });
        setUserRollup(null);
      })
      .finally(() => {
        setUserLoading(false);
      });
  };

  const handleTeamLookup = () => {
    const teamId = parseInt(teamIdInput, 10);
    if (isNaN(teamId)) {
      setMsgConfig({
        type: 'error',
        message: 'Please enter a valid numeric Team ID.',
        snackFlag: true,
      });
      return;
    }
    setTeamLoading(true);
    fetchTeamUsage(teamId, getFromStr(), getToStr())
      .then((data) => {
        setTeamRollup(data);
      })
      .catch(() => {
        setMsgConfig({
          type: 'error',
          message: 'Failed to load team usage. Check the Team ID.',
          snackFlag: true,
        });
        setTeamRollup(null);
      })
      .finally(() => {
        setTeamLoading(false);
      });
  };

  const breakdownColumns: GridColDef[] = [
    {
      field: 'userEmail',
      headerName: 'User Email',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'totalCost',
      headerName: 'Total Cost',
      width: 140,
      renderCell: (params) => formatCurrency(params.value),
    },
    {
      field: 'interactionCount',
      headerName: 'Interactions',
      width: 130,
    },
    {
      field: 'averageCostPerInteraction',
      headerName: 'Avg Cost',
      width: 140,
      renderCell: (params) => formatCurrency(params.value),
    },
  ];

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box className="admin-usage">
        <Box className="admin-usage__header">
          <Typography variant="h5">Usage & Billing</Typography>
          <Box className="admin-usage__date-range">
            <DatePicker
              label="From"
              value={dateFrom}
              onChange={(val) => val && setDateFrom(val)}
              format="YYYY-MM-DD"
              slotProps={{ textField: { size: 'small' } }}
            />
            <DatePicker
              label="To"
              value={dateTo}
              onChange={(val) => val && setDateTo(val)}
              format="YYYY-MM-DD"
              slotProps={{ textField: { size: 'small' } }}
            />
            <Button variant="contained" onClick={handleDateChange}>
              Apply
            </Button>
          </Box>
        </Box>

        {/* Platform Summary */}
        <Box className="admin-usage__section">
          <Typography variant="h6" className="admin-usage__section-title">
            Platform Summary
          </Typography>
          {summaryLoading ? (
            <Box className="admin-usage__loading">
              <CircularProgress />
            </Box>
          ) : summary ? (
            <Box className="admin-usage__summary-cards">
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Total Cost
                  </Typography>
                  <Typography variant="h4">
                    {formatCurrency(summary.totalCost)}
                  </Typography>
                </CardContent>
              </Card>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Total Interactions
                  </Typography>
                  <Typography variant="h4">
                    {summary.interactionCount}
                  </Typography>
                </CardContent>
              </Card>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Avg Cost / Interaction
                  </Typography>
                  <Typography variant="h4">
                    {formatCurrency(summary.averageCostPerInteraction)}
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          ) : null}
        </Box>

        {/* Per-User Lookup */}
        <Box className="admin-usage__section">
          <Typography variant="h6" className="admin-usage__section-title">
            User Usage Lookup
          </Typography>
          <Box className="admin-usage__lookup-form">
            <TextField
              label="User ID"
              size="small"
              value={userIdInput}
              onChange={(e) => setUserIdInput(e.target.value)}
              type="number"
            />
            <Button
              variant="outlined"
              startIcon={<SearchIcon />}
              onClick={handleUserLookup}
              disabled={userLoading}
            >
              Look Up
            </Button>
          </Box>
          {userLoading && (
            <Box className="admin-usage__loading">
              <CircularProgress size={24} />
            </Box>
          )}
          {userRollup && !userLoading && (
            <Box className="admin-usage__result-cards">
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    User
                  </Typography>
                  <Typography variant="body1">{userRollup.userEmail}</Typography>
                </CardContent>
              </Card>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Total Cost
                  </Typography>
                  <Typography variant="h5">
                    {formatCurrency(userRollup.totalCost)}
                  </Typography>
                </CardContent>
              </Card>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Interactions
                  </Typography>
                  <Typography variant="h5">
                    {userRollup.interactionCount}
                  </Typography>
                </CardContent>
              </Card>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Avg Cost
                  </Typography>
                  <Typography variant="h5">
                    {formatCurrency(userRollup.averageCostPerInteraction)}
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          )}
        </Box>

        {/* Per-Team Lookup */}
        <Box className="admin-usage__section">
          <Typography variant="h6" className="admin-usage__section-title">
            Team Usage Lookup
          </Typography>
          <Box className="admin-usage__lookup-form">
            <TextField
              label="Team ID"
              size="small"
              value={teamIdInput}
              onChange={(e) => setTeamIdInput(e.target.value)}
              type="number"
            />
            <Button
              variant="outlined"
              startIcon={<SearchIcon />}
              onClick={handleTeamLookup}
              disabled={teamLoading}
            >
              Look Up
            </Button>
          </Box>
          {teamLoading && (
            <Box className="admin-usage__loading">
              <CircularProgress size={24} />
            </Box>
          )}
          {teamRollup && !teamLoading && (
            <>
              <Box className="admin-usage__result-cards">
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Team
                    </Typography>
                    <Typography variant="body1">{teamRollup.teamName}</Typography>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Total Cost
                    </Typography>
                    <Typography variant="h5">
                      {formatCurrency(teamRollup.totalCost)}
                    </Typography>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Interactions
                    </Typography>
                    <Typography variant="h5">
                      {teamRollup.interactionCount}
                    </Typography>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Avg Cost
                    </Typography>
                    <Typography variant="h5">
                      {formatCurrency(teamRollup.averageCostPerInteraction)}
                    </Typography>
                  </CardContent>
                </Card>
              </Box>

              {/* Per-user breakdown table */}
              <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                Per-User Breakdown
              </Typography>
              <Box className="admin-usage__grid">
                <DataGrid
                  rows={teamRollup.userBreakdowns}
                  columns={breakdownColumns}
                  getRowId={(row: UserBreakdown) => row.userId}
                  pageSizeOptions={[10, 25, 50]}
                  initialState={{
                    pagination: { paginationModel: { pageSize: 10 } },
                  }}
                  disableRowSelectionOnClick
                  autoHeight
                />
              </Box>
            </>
          )}
        </Box>

        {msgConfig.snackFlag && <SnackBarMessage snacBarValue={msgConfig} />}
      </Box>
    </LocalizationProvider>
  );
};

export default AdminUsage;
